/**
 * Shared wording for the "validation in progress" banners.
 *
 * The banners used to read "Processing 0 process steps... Starting..." for an
 * entire run: the step count came only from the poll response, which omits it
 * until a validation row exists, and the right-hand label showed "Starting..."
 * whenever completed_steps was undefined — which is the whole run when the
 * BullMQ job cannot be located. Measured runtime is ~44s for 21 steps, so
 * "may take several minutes" also read as a hang.
 */

export interface ValidationProgressLike {
  completed_steps?: number;
  total_steps?: number;
  estimated_progress?: number;
  progress_percentage?: number;
  started_at?: string | null;
}

const SECONDS_PER_STEP = 2.2;
const MIN_ESTIMATE_SECONDS = 20;

export const countMapSteps = (mapData: any): number => {
  const processes = mapData?.elements?.processes || mapData?.processes;
  return Array.isArray(processes) ? processes.length : 0;
};

export const resolveTotalSteps = (
  progress: ValidationProgressLike | null | undefined,
  fallbackTotal = 0,
): number => {
  const fromPoll = Number(progress?.total_steps);
  if (Number.isFinite(fromPoll) && fromPoll > 0) return fromPoll;
  return fallbackTotal > 0 ? fallbackTotal : 0;
};

export const estimateValidationSeconds = (totalSteps: number): number =>
  Math.max(MIN_ESTIMATE_SECONDS, Math.round(totalSteps * SECONDS_PER_STEP));

const formatDuration = (seconds: number): string => {
  if (seconds < 60) return `${seconds} seconds`;
  const minutes = Math.round(seconds / 60);
  return minutes === 1 ? 'about a minute' : `about ${minutes} minutes`;
};

const elapsedSeconds = (startedAt?: string | null): number | null => {
  if (!startedAt) return null;
  const started = new Date(startedAt).getTime();
  if (!Number.isFinite(started)) return null;
  const seconds = Math.floor((Date.now() - started) / 1000);
  return seconds >= 0 ? seconds : null;
};

export const validationPercent = (
  progress: ValidationProgressLike | null | undefined,
  totalSteps: number,
): number | null => {
  const explicit = progress?.progress_percentage ?? progress?.estimated_progress;
  if (typeof explicit === 'number' && Number.isFinite(explicit)) {
    return Math.max(0, Math.min(100, Math.round(explicit)));
  }
  const completed = progress?.completed_steps;
  if (typeof completed === 'number' && totalSteps > 0) {
    return Math.max(0, Math.min(100, Math.round((completed / totalSteps) * 100)));
  }
  return null;
};

/** Right-hand label: names the step being worked on instead of a frozen "Starting...". */
export const validationStepLabel = (
  progress: ValidationProgressLike | null | undefined,
  totalSteps: number,
): string => {
  const completed = progress?.completed_steps;
  if (typeof completed === 'number' && totalSteps > 0) {
    if (completed >= totalSteps) return `Finishing ${totalSteps} of ${totalSteps}`;
    return `Validating step ${completed + 1} of ${totalSteps}`;
  }
  if (totalSteps > 0) return `Preparing ${totalSteps} steps`;
  return 'Starting';
};

export interface ValidationHeadline {
  match_percentage: number | null;
  needs_review: number;
}

/**
 * Headline figures for the validation-bar chip that replaced the in-page
 * Validation Report panel. "Needs review" is everything the challenger did not
 * mark a clear match, which is the count the Step Validation modal shows as
 * "Need Review / Requires action".
 *
 * The count MUST come from the backend's `needs_action` field: it is computed
 * live from validation_results + gap_detection_results (see
 * backend/src/utils/validationCounts.js) so it matches the Step Validation
 * panel exactly. The stored `summary.needs_review` snapshot cannot be trusted —
 * it goes stale once a user actions a step, ignores ERROR-status steps, and
 * omits gaps entirely. We only fall back to the summary while a freshly
 * deployed backend has not yet started returning `needs_action`.
 */
export const summariseValidation = (data: any): ValidationHeadline | null => {
  if (!data?.has_validation) return null;
  const summary = data.summary || {};
  const needsReview = typeof data.needs_action === 'number'
    ? data.needs_action
    : Number(summary.needs_review) || 0;
  // Number(null) is 0, so a validation that has not scored yet would claim
  // "0% match" — which reads as a failed run rather than a missing figure.
  const raw = data.match_percentage;
  const match = raw === null || raw === undefined || raw === '' ? NaN : Number(raw);
  return {
    match_percentage: Number.isFinite(match) ? Math.round(match) : null,
    needs_review: needsReview,
  };
};

/** Sub-line: real step count, a runtime estimate that matches reality, and elapsed time. */
export const validationDetailLine = (
  progress: ValidationProgressLike | null | undefined,
  totalSteps: number,
): string => {
  const parts: string[] = [];
  parts.push(
    totalSteps > 0
      ? `${totalSteps} process ${totalSteps === 1 ? 'step' : 'steps'} — usually ${formatDuration(estimateValidationSeconds(totalSteps))} for a map this size.`
      : 'Preparing the validation run.',
  );
  const elapsed = elapsedSeconds(progress?.started_at);
  if (elapsed !== null) parts.push(`${elapsed}s elapsed.`);
  return parts.join(' ');
};
